import gql from "graphql-tag";
import {useCallback, useMemo, useState} from "react";

import {FetchPolicy, type Result} from "@khanacademy/wonder-blocks-data";
import {SchedulePolicy, useTimeout} from "@khanacademy/wonder-blocks-timing";
import {useQuery} from "@khan/data-gql";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import type {CooldownStatus} from "@khan/guide-types";
import {useRealTimeMessaging} from "@khan/real-time-messaging";

import {
    DEFAULT_COOLDOWN_RESPONSE_NON_SUCCESS,
    DEFAULT_COOLDOWN_STATUS,
} from "../internal/constants.ts";

import type {
    getCooldownStatus_user_cooldownStatus as GQLCooldownStatus,
    getCooldownStatus_user as GQLUser,
} from "./__graphql-types__/getCooldownStatus.ts";
import type {getCooldownStatusType} from "./__graphql-types__/index.ts";

export const USE_COOLDOWN_STATUS_QUERY: GqlOperation<
    getCooldownStatusType["response"],
    getCooldownStatusType["variables"]
> = gqlOp(gql`
    query getCooldownStatus {
        user {
            id
            hasCooldown
            cooldownStatus {
                hasAntiCheatCooldown
                lastAntiCheatCooldownExpiresAt
                hasTeacherDirectedCooldown
                teacherDirectedCooldownExpiresAt
                teacherDirectedCooldownStartedBy {
                    id
                    kaid
                    nickname
                }
                expiresInSeconds
            }
        }
    }
`);

export interface CooldownStatusResponse {
    status: string;
    hasCooldown: boolean;
    cooldownStatus: CooldownStatus;
}

interface Props {
    skip: boolean;
}

type CooldownResult =
    | Result<{user: GQLUser | null | undefined}>
    | {status: "expired"};

function getExpiresInSeconds(result: CooldownResult): number {
    if (result.status !== "success") {
        return 0;
    }

    return result.data.user?.cooldownStatus?.expiresInSeconds ?? 0;
}

export const useFetchCooldownStatus = (
    props: Props,
): CooldownStatusResponse => {
    const {skip = false} = props;

    // Set when a fetched cooldown has expired. When this is set we override
    // the GraphQL query result and return a cooldown status below that's the
    // same as if there was no data (i.e. the same as if query was skipped).
    const [hasExpired, setHasExpired] = useState(false);

    const [networkResult, refetch] = useQuery(USE_COOLDOWN_STATUS_QUERY, {
        fetchPolicy: FetchPolicy.NetworkOnly,
        skip: skip,
    });

    const result = hasExpired ? {status: "expired" as const} : networkResult;

    const clearHasExpiredAndRefetch = useCallback(() => {
        setHasExpired(false);
        if (!skip) {
            refetch();
        }
    }, [skip, refetch, setHasExpired]);

    const realTimeCallbacks = useMemo(
        () => ({
            onConnect: () => clearHasExpiredAndRefetch(),
            onMessage: () => clearHasExpiredAndRefetch(),
            onReset: () => clearHasExpiredAndRefetch(),
        }),
        [clearHasExpiredAndRefetch],
    );

    // Anytime there's a cooldown status update, re-fetch the cooldown status
    // to get the latest info.
    useRealTimeMessaging("cooldown-status-updates", realTimeCallbacks);

    const expiresInSeconds = getExpiresInSeconds(result);

    // When the cooldown has expired we can trust there's no more cooldown and
    // use the no-data case below (via the skip parameter to useQuery). If
    // the cooldown has been updated (or is updated in the future), we'll
    // receive a real-time message and refetch the cooldown data.
    useTimeout(() => setHasExpired(true), expiresInSeconds * 1000, {
        schedulePolicy:
            result.status === "success" && expiresInSeconds > 0
                ? SchedulePolicy.Immediately
                : SchedulePolicy.OnDemand,
    });

    switch (result.status) {
        case "loading":
        case "error":
        case "aborted":
        case "no-data":
        case "expired":
            return {
                ...DEFAULT_COOLDOWN_RESPONSE_NON_SUCCESS,
                status: result.status,
            };
        case "success":
            const hasCooldown = result.data.user?.hasCooldown ?? false;
            const cooldownStatus =
                result.data.user?.cooldownStatus ?? undefined;
            const mappedStatus = mapCooldownStatus(cooldownStatus);

            if (mappedStatus) {
                return {
                    status: result.status,
                    hasCooldown,
                    cooldownStatus: mappedStatus,
                };
            }

            // Fallback case - use stable default but override hasCooldown
            if (hasCooldown === false) {
                return {
                    status: result.status,
                    hasCooldown: false,
                    cooldownStatus: DEFAULT_COOLDOWN_STATUS,
                };
            }

            // Only create new object if hasCooldown is true (rare case)
            return {
                status: result.status,
                hasCooldown: true,
                cooldownStatus: {
                    ...DEFAULT_COOLDOWN_STATUS,
                    hasCooldown: true,
                },
            };
    }
};

function mapCooldownStatus(
    status: GQLCooldownStatus | undefined,
): CooldownStatus | null {
    if (!status) {
        return null;
    }

    if (status.hasTeacherDirectedCooldown) {
        // Teacher-directed cooldowns take precedence over anti-cheat cooldowns.
        return {
            hasCooldown: true,
            cooldownType: "TeacherDirected",
            expiresAt: status.teacherDirectedCooldownExpiresAt
                ? new Date(status.teacherDirectedCooldownExpiresAt)
                : null,
            teacherKaid: status.teacherDirectedCooldownStartedBy?.kaid ?? null,
            teacherName:
                status.teacherDirectedCooldownStartedBy?.nickname ?? null,
        };
    }
    if (status.hasAntiCheatCooldown) {
        return {
            hasCooldown: true,
            cooldownType: "AntiCheat",
            expiresAt: status.lastAntiCheatCooldownExpiresAt
                ? new Date(status.lastAntiCheatCooldownExpiresAt)
                : null,
            teacherKaid: null,
            teacherName: null,
        };
    }

    // Return stable default for no cooldown case
    return DEFAULT_COOLDOWN_STATUS;
}
